/**
 * File: bfwt-custom-slider.js
 * PURE CSS SCROLL SNAP VERSION (Performance Optimized)
 *
 * Centralized Slider Manager to handle:
 * 1. Single IntersectionObserver for initialization (Lazy Init)
 * 2. Single ResizeObserver for responsive adaptation
 * 3. Delegated Events for Navigation
 * 4. Localized, Passive Scroll Listeners for Dot Sync
 */
(function () {
  'use strict';

  class SliderManager {
    constructor() {
      this.sliders = new WeakMap(); // Store instance data
      this.observer = null;
      this.resizeObserver = null;
      this.init();
    }

    init() {
      // 1. Shared IntersectionObserver for Initialization
      // Only initializes sliders when they approach the viewport
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.activateSlider(entry.target);
              this.observer.unobserve(entry.target);
            }
          }
        },
        { rootMargin: '200px 0px' }
      );

      // 2. Shared ResizeObserver for Responsive Dots
      let lastWidth = window.innerWidth;
      this.resizeObserver = new ResizeObserver((entries) => {
        const currentWidth = window.innerWidth;
        if (currentWidth === lastWidth) {
          return;
        }
        lastWidth = currentWidth;

        for (const entry of entries) {
          // entry.target is the track
          const slider = entry.target.closest('.bf-custom-slider');
          if (slider && this.sliders.has(slider)) {
            this.updatePagination(slider);
          }
        }
      });

      // 3. Global Event Delegation (Click)
      document.addEventListener('click', (e) => {
        const navBtn = e.target.closest('.bf-custom-slider__arrow');
        const dotBtn = e.target.closest('.bf-custom-slider__dot');

        if (navBtn) {
          const direction = navBtn.classList.contains(
            'bf-custom-slider__arrow--next'
          )
            ? 'next'
            : 'prev';
          this.handleNav(navBtn, direction);
        } else if (dotBtn) {
          this.handleDot(dotBtn);
        }
      });

      // 4. Initial Scan
      this.scan();

      // 5. Lightweight DOM Observer (Optional - mainly for AJAX content)
      // Replaced heavy recursive body observer with a specific one if needed,
      // or rely on 'bfwt:content-loaded' event for dynamic content.
      document.addEventListener('bfwt:content-loaded', (e) => {
        const container = e.detail?.target || document;
        this.scan(container);
      });
    }

    scan(context = document) {
      const sliders = context.querySelectorAll(
        '.bf-custom-slider:not([data-managed])'
      );
      sliders.forEach((slider) => {
        slider.dataset.managed = 'true';
        this.observer.observe(slider);
      });
    }

    activateSlider(slider) {
      const track = slider.querySelector('.bf-custom-slider__track');
      if (!track) return;

      // Mark as valid and store data
      this.sliders.set(slider, { track });

      // Build Dots initially
      this.buildDots(slider, track);

      // Observe resize
      this.resizeObserver.observe(track);

      // Attach Local Passive Scroll Listener for Dot Sync
      track.addEventListener('scroll', () => {
        this.handleScroll(track);
      }, { passive: true });

      // Autoplay initialization
      this.setupAutoplay(slider, track);
    }

    setupAutoplay(slider, track) {
      if (slider.dataset.autoplay !== 'true') return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const intervalMs = parseInt(slider.dataset.autoplayInterval, 10) || 5000;
      let timer = null;
      let isPaused = false;

      const start = () => {
        if (timer || isPaused) return;
        timer = setInterval(() => {
          if (!isPaused && document.visibilityState === 'visible') {
            this.handleNav(track, 'next');
          }
        }, intervalMs);
      };

      const stop = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };

      slider.addEventListener('mouseenter', () => {
        isPaused = true;
        stop();
      });

      slider.addEventListener('mouseleave', () => {
        isPaused = false;
        start();
      });

      slider.addEventListener('touchstart', () => {
        isPaused = true;
        stop();
      }, { passive: true });

      slider.addEventListener('touchend', () => {
        isPaused = false;
        start();
      }, { passive: true });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          stop();
        } else if (!isPaused) {
          start();
        }
      });

      start();
    }

    // --- Logic ---

    getVisibleCols(slider) {
      const width = window.innerWidth;
      if (width >= 1024)
        {return parseInt(
          getComputedStyle(slider).getPropertyValue('--grid-cols-desktop') || 4
        );}
      if (width >= 768)
        {return parseInt(
          getComputedStyle(slider).getPropertyValue('--grid-cols-tablet') || 3
        );}
      return parseInt(
        getComputedStyle(slider).getPropertyValue('--grid-cols-mobile') || 2
      );
    }

    buildDots(slider, track) {
      const pagination = slider.querySelector('.bf-custom-slider__pagination');
      if (!pagination) return;

      const slides = track.children.length;
      if (slides === 0) return;

      const visibleCols = this.getVisibleCols(slider);
      const totalPages = Math.ceil(slides / visibleCols);

      // If only 1 page, hide pagination, else ensure it's visible (if CSS allows)
      if (totalPages <= 1) {
        pagination.innerHTML = '';
        return;
      }

      if (pagination.childElementCount === totalPages) return; // No change

      pagination.innerHTML = '';

      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('button');
        dot.className = 'bf-custom-slider__dot';
        dot.dataset.index = i;
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        if (i === 0) dot.classList.add('is-active');
        pagination.appendChild(dot);
      }
    }

    updatePagination(slider) {
      const track = slider.querySelector('.bf-custom-slider__track');
      if (!track) return;

      this.buildDots(slider, track);
      this.syncActiveDot(slider, track);
    }

    syncActiveDot(slider, track) {
      const pagination = slider.querySelector('.bf-custom-slider__pagination');
      if (!pagination || !pagination.children.length) return;

      const scrollLeft = track.scrollLeft;
      const itemWidth = track.firstElementChild?.offsetWidth || 300;
      const gap = parseInt(getComputedStyle(track).gap || 0);
      const visibleCols = this.getVisibleCols(slider);

      const onePageScroll = (itemWidth + gap) * visibleCols;
      let pageIndex = Math.round(scrollLeft / onePageScroll);

      const totalPages = pagination.children.length;
      if (pageIndex >= totalPages) pageIndex = totalPages - 1;
      if (pageIndex < 0) pageIndex = 0;

      const dots = pagination.children;
      const currentActive = pagination.querySelector('.is-active');

      if (currentActive !== dots[pageIndex]) {
        if (currentActive) currentActive.classList.remove('is-active');
        if (dots[pageIndex]) dots[pageIndex].classList.add('is-active');
      }
    }

    // --- Handlers ---

    handleNav(btn, direction) {
      const slider = btn.closest('.bf-custom-slider');
      const track = slider.querySelector('.bf-custom-slider__track');
      if (!track) return;

      const itemWidth = track.firstElementChild?.offsetWidth || 300;
      const gap = parseInt(getComputedStyle(track).gap || 0);
      const visibleCols = this.getVisibleCols(slider);

      const scrollAmount = (itemWidth + gap) * visibleCols;
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

      if (direction === 'next') {
        if (track.scrollLeft >= maxScroll - 25) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      } else {
        if (track.scrollLeft <= 25) {
          track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
      }
    }

    handleDot(dot) {
      const slider = dot.closest('.bf-custom-slider');
      const track = slider.querySelector('.bf-custom-slider__track');
      const index = parseInt(dot.dataset.index);
      const visibleCols = this.getVisibleCols(slider);

      const itemWidth = track.firstElementChild?.offsetWidth || 300;
      const gap = parseInt(getComputedStyle(track).gap || 0);

      const targetSlide = index * visibleCols;
      const targetScroll = targetSlide * (itemWidth + gap);

      track.scrollTo({ left: targetScroll, behavior: 'smooth' });
    }

    handleScroll(track) {
      // Simple throttle using RequestAnimationFrame or Timeout
      if (track.isScrolling) return;

      track.isScrolling = true;
      window.requestAnimationFrame(() => {
        const slider = track.closest('.bf-custom-slider');
        this.syncActiveDot(slider, track);
        track.isScrolling = false;
      });
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new SliderManager());
  } else {
    new SliderManager();
  }
})();
